// Dot-peen DataMatrix recovery for the photo fallback (frame E).
//
// Some cartons carry their GS1 DataMatrix as discrete laser-etched dots on a
// dark panel rather than as solid modules. ZXing's DataMatrix decoder expects
// contiguous modules, so the gaps between dots defeat it — at every
// combination of its own options (tryInvert, tryRotate, tryDownscale,
// tryHarder, all four binarizers) and its experimental tryDenoise, which
// recovered none of the failing cartons in the client photo corpus.
//
// A morphological close — dilate then erode — merges the dots back into
// modules without moving the module boundaries the way a bare dilate does.
// Erosion is not optional: measured against that corpus, a bare dilate
// recovered 4 of 10 at radius 2 and destroyed the symbol outright at radius 3.
//
// The structuring element is a square, which makes the close separable: a
// (2r+1)x(2r+1) operation becomes four one-dimensional passes rather than a
// (2r+1)^2-tap kernel. At radius 2 — a 5x5 close, applied at native
// resolution — every failing carton in the corpus decodes.

export const DOT_PEEN_RADIUS = 2;

// Matches the reference pipeline the radius was measured against
// (ImageMagick `-threshold 45%`).
const THRESHOLD_RATIO = 0.45;

// Rec. 601 luma, the same weighting a canvas grayscale filter applies.
const LUMA_R = 0.299;
const LUMA_G = 0.587;
const LUMA_B = 0.114;

// Binarise in place, then close. `imageData` is mutated and returned so the
// caller can hand it straight to the reader.
export function closeDots(imageData, radius = DOT_PEEN_RADIUS) {
  const { width, height } = imageData;
  if (width === 0 || height === 0) return imageData;

  const source = thresholdedGray(imageData);
  const scratch = new Uint8Array(source.length);

  // Dilate (max), then erode (min) — each separated into a horizontal pass
  // and a vertical one. Every pass writes to a buffer it is not reading, so
  // no pass can consume its own partial output.
  sweepHorizontal(source, scratch, width, height, radius, true);
  sweepVertical(scratch, source, width, height, radius, true);
  sweepHorizontal(source, scratch, width, height, radius, false);
  sweepVertical(scratch, source, width, height, radius, false);

  writeGray(imageData, source);
  return imageData;
}

// Grayscale, stretch the range (ImageMagick `-auto-level`), then threshold.
// Auto-level matters because these panels are photographed under fridge
// lighting: without it a dim shot never reaches a fixed threshold at all.
function thresholdedGray({ data, width, height }) {
  const gray = new Uint8Array(width * height);
  let min = 255;
  let max = 0;

  for (let i = 0, p = 0; p < gray.length; i += 4, p++) {
    const value = (LUMA_R * data[i] + LUMA_G * data[i + 1] + LUMA_B * data[i + 2]) | 0;
    gray[p] = value;
    if (value < min) min = value;
    if (value > max) max = value;
  }

  const span = max - min;
  const cutoff = THRESHOLD_RATIO * 255;
  for (let p = 0; p < gray.length; p++) {
    const levelled = span > 0 ? ((gray[p] - min) * 255) / span : gray[p];
    gray[p] = levelled > cutoff ? 255 : 0;
  }
  return gray;
}

// One-dimensional min/max over a window of 2*radius+1, clamped at the edges so
// the border does not erode inward.
function sweepHorizontal(source, target, width, height, radius, takeMax) {
  for (let y = 0; y < height; y++) {
    const row = y * width;
    for (let x = 0; x < width; x++) {
      const from = x - radius < 0 ? 0 : x - radius;
      const to = x + radius >= width ? width - 1 : x + radius;
      let best = source[row + from];
      for (let i = from + 1; i <= to; i++) {
        const value = source[row + i];
        if (takeMax ? value > best : value < best) best = value;
      }
      target[row + x] = best;
    }
  }
}

function sweepVertical(source, target, width, height, radius, takeMax) {
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      const from = y - radius < 0 ? 0 : y - radius;
      const to = y + radius >= height ? height - 1 : y + radius;
      let best = source[from * width + x];
      for (let i = from + 1; i <= to; i++) {
        const value = source[i * width + x];
        if (takeMax ? value > best : value < best) best = value;
      }
      target[y * width + x] = best;
    }
  }
}

function writeGray(imageData, gray) {
  const { data } = imageData;
  for (let i = 0, p = 0; p < gray.length; i += 4, p++) {
    data[i] = data[i + 1] = data[i + 2] = gray[p];
    data[i + 3] = 255;
  }
}
